import { useCallback, useState } from 'react';

import { useTableData } from '@/shared/hooks/useTableData';
import {
  getAdminToolsListDisplayDescription,
  getAdminToolsListDisplayName,
} from '@/features/admin/lib/canonicalAdminToolLabels';

import type { SortDirection } from '@/shared/ui/data-table';

import type { ToolRowData } from '../ToolsTable/ToolsTable.types';
import type { ToolsTableV2Props, ToolsTableV2RowData } from './ToolsTableV2.types';

export const TOOLS_PAGE_SIZE = 25;

// `useTableData` widens its direction to MUI's, which admits `undefined`; the table's header
// does not, and the list is never unsorted, so ascending stands in for the absent case.
const ASCENDING: SortDirection = 'asc';

type UseToolsTableV2Options = Pick<ToolsTableV2Props, 'tools' | 'typeFilter'> & {
  searchQuery: string;
};

export function useToolsTableV2({ tools, typeFilter, searchQuery }: UseToolsTableV2Options) {
  const query = searchQuery.trim().toLowerCase();

  // The search runs inside the transform so it can match `displayName` — the
  // label the row prints — rather than the raw API name behind it.
  const transform = useCallback(
    (rows: ToolRowData[]): ToolsTableV2RowData[] =>
      rows
        .map((tool) => ({
          ...tool,
          displayName: getAdminToolsListDisplayName(tool.id, tool.name),
          displayDescription: getAdminToolsListDisplayDescription(tool.id, tool.description),
        }))
        .filter((tool) => tool.displayName.toLowerCase().includes(query)),
    [query],
  );

  const tableData = useTableData<ToolRowData, ToolsTableV2RowData>({
    data: tools,
    transform,
    initialSort: { key: 'displayName', direction: ASCENDING },
    initialRowsPerPage: TOOLS_PAGE_SIZE,
  });

  const { handlePageChange } = tableData;

  // A filter or search change replaces the list, so page 4 of the old one means nothing. The
  // reset is derived from the props during render rather than run from an effect: the state
  // being corrected belongs to this component, so React re-runs the render with the new page
  // before anything is committed, and the reader never sees the stale page paint first.
  const narrowing = `${typeFilter}\u0000${query}`;
  const [narrowingOnScreen, setNarrowingOnScreen] = useState(narrowing);
  if (narrowingOnScreen !== narrowing) {
    setNarrowingOnScreen(narrowing);
    handlePageChange(0);
  }

  const goToPage = useCallback(
    (page: number) => {
      handlePageChange(page);
    },
    [handlePageChange],
  );

  return {
    rows: tableData.paginatedData,
    totalCount: tableData.totalCount,
    page: tableData.page,
    rowsPerPage: tableData.rowsPerPage,
    sortDirection: tableData.sortDirection,
    /** Name is the only sortable column, so the toggle needs no argument. */
    toggleNameSort: () => {
      tableData.toggleSort('displayName');
    },
    goToPage,
    showType: typeFilter === 'all',
    /** The reader has narrowed the list themselves, so an empty result is theirs to undo. */
    isNarrowed: typeFilter !== 'all' || query !== '',
  };
}
